/* OLVR 360 viewer controller (Thinkspace embed), Oct 2026.
   Loaded only on the ring builder (custom liquid block in
   templates/product.builder.json). Remove that script tag to undo everything.

   What it changes:
   1. Metal or hidden stone change: sent to the 360 as a message (Thinkspace
      postMessage "full state update"). Instant, no reload.
   2. Diamond shape change: a different 3D model, which Thinkspace can't swap
      at runtime. The new model loads behind the current ring and replaces it
      when Thinkspace reports loading_complete, so the shopper keeps seeing a
      ring the whole time. The other shapes are warmed up in advance (see
      PRELOAD) so that load is quicker.
   3. The 3D thumbnail (first tile) follows shape + metal from the Front photo
      map in #olvr-front-map. Built from photo file names, not the variant list,
      so it works past Shopify's 250 variant limit.

   How it hooks in: layout/theme.liquid still builds the Thinkspace URL on every
   option change and sets iframe.src. This file intercepts that src set on the
   viewer iframe and decides between a recolour message and a background swap.
   If this file isn't loaded, theme.liquid's old behaviour (reload) applies. */
(function () {
  if (window.OLVRViewer) return;

  var SWAP_TIMEOUT_MS = 10000; /* KNOB: show the new model anyway if loading_complete never arrives */
  var FADE_MS = 180;           /* KNOB: cross-fade between old and new model */
  /* Shape warm-up (Oct 22, light version). Once the first ring has loaded,
     each other diamond shape for this style is loaded once in a hidden frame,
     one at a time, and that frame is thrown away as soon as it finishes. The
     model files stay in the browser cache, so when the shopper picks that
     shape it loads in about 1.4s instead of 2.8 to 3.6s. At most one extra 3D
     viewer exists at any moment, and only for a few seconds.
     (The first try kept all 6 viewers alive for instant swaps. It froze the
     page after a few minutes of browsing, so don't go back to that.)
     Hovering a shape moves it to the front of the queue. Set PRELOAD to false
     to turn warm-up off.
     Phones (Oct 23): warm-up is off on phones. Each warm-up is a whole extra 3D
     viewer, and on iPhones that churn went with the 360 coming up zoomed in and,
     after a few style changes, Safari showing "A problem repeatedly occurred"
     (the page ran out of memory). Phones load each shape when it's picked. */
  var PHONE = window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
  var PRELOAD = !PHONE;              /* KNOB */
  var PRELOAD_START_MS = 800;        /* KNOB: wait after the first ring loads */
  var MAX_FRAMES = 2;                /* the ring on screen plus one loading behind it. Don't raise. */
  var SHAPE_CODE = { cushion: 'csh', emerald: 'emd', marquise: 'mrq', oval: 'ovl', radiant: 'rad', round: 'rnd' };

  var proto = Object.getOwnPropertyDescriptor(HTMLIFrameElement.prototype, 'src');
  function nativeGet(el) { return proto.get.call(el); }
  function nativeSet(el, v) { proto.set.call(el, v); }

  var frames = {};     /* model -> { el, model, metal, secret, ready, loaded, used } */
  var active = null;   /* frame on screen */
  var waiting = null;  /* { model, timer } shape the shopper picked that is still loading */
  var desired = null;  /* { model, metal, secret, src } last thing the shopper picked */
  var queue = [];      /* models still to preload */
  var preloadStarted = false;
  var warmed = {};     /* model -> true once its files are in the browser cache */

  function parseSrc(src) {
    try {
      var u = new URL(src, location.href);
      return {
        model: u.searchParams.get('part1') || '',
        metal: u.searchParams.get('part1Color') || '',
        secret: u.searchParams.get('part1g2') || ''
      };
    } catch (e) { return null; }
  }

  function srcFor(model) {
    try {
      var base = (desired && desired.src) || nativeGet(active.el);
      var u = new URL(base, location.href);
      u.searchParams.set('part1', model);
      return u.toString();
    } catch (e) { return null; }
  }

  function post(rec, metal, secret) {
    if (!rec || !rec.el || !rec.el.contentWindow) return;
    rec.el.contentWindow.postMessage({
      parts: { part1: { colors: { tone1: metal, gems: { g2: secret } } } }
    }, '*');
    rec.metal = metal;
    rec.secret = secret;
  }

  function reconcile(rec) {
    if (!rec || !rec.ready || !desired || rec.model !== desired.model) return;
    if (rec.metal !== desired.metal || rec.secret !== desired.secret) post(rec, desired.metal, desired.secret);
  }

  /* Make el.src = x go through us instead of reloading the frame. */
  function intercept(el) {
    if (el.__olvrIntercepted) return;
    el.__olvrIntercepted = true;
    Object.defineProperty(el, 'src', {
      configurable: true,
      get: function () { return (desired && active && active.el === el) ? desired.src : nativeGet(el); },
      set: function (v) { if (!route(v)) nativeSet(el, v); }
    });
  }

  function adopt(el) {
    var st = parseSrc(el.getAttribute('src') || nativeGet(el)) || { model: '', metal: '', secret: '' };
    active = { el: el, model: st.model, metal: st.metal, secret: st.secret, ready: false, loaded: false, used: Date.now() };
    if (st.model) frames[st.model] = active;
    intercept(el);
    /* The viewer may have finished loading before this ran. Assume it's ready
       after a short grace period so an early change isn't lost. */
    setTimeout(function () { if (active && active.el === el && !active.ready) { active.ready = true; reconcile(active); } }, 2500);
    setTimeout(function () { if (frames[st.model] && !frames[st.model].loaded) { frames[st.model].loaded = true; startPreload(); } }, 8000);
  }

  /* Remove a frame that isn't on screen, so its 3D viewer is freed. */
  function discard(rec) {
    if (!rec || rec === active) return;
    if (rec.loaded) warmed[rec.model] = true;
    if (frames[rec.model] === rec) delete frames[rec.model];
    if (rec.el.parentNode) rec.el.parentNode.removeChild(rec.el);
  }

  function makeFrame(model) {
    var cur = active && active.el;
    var src = srcFor(model);
    if (!cur || !cur.parentNode || !src) return null;
    var el = document.createElement('iframe');
    el.setAttribute('allow', cur.getAttribute('allow') || 'fullscreen; xr-spatial-tracking');
    el.setAttribute('title', '3D ring viewer');
    el.id = 'mesh3d-viewer-' + model;
    el.style.cssText = cur.style.cssText;
    el.style.background = 'white';
    el.style.touchAction = 'pan-y';
    el.style.opacity = '0';
    el.style.visibility = 'visible'; /* must be drawable while loading or it never loads */
    el.style.transition = 'opacity ' + FADE_MS + 'ms ease';
    el.style.pointerEvents = 'none';
    el.style.zIndex = '0';
    nativeSet(el, src);
    cur.parentNode.appendChild(el);
    var st = parseSrc(src) || {};
    var rec = { el: el, model: model, metal: st.metal, secret: st.secret, ready: false, loaded: false, used: 0 };
    frames[model] = rec;
    intercept(el);
    evict();
    return rec;
  }

  function evict() {
    var list = Object.keys(frames).map(function (k) { return frames[k]; });
    if (list.length <= MAX_FRAMES) return;
    list.filter(function (r) { return r !== active && !(waiting && waiting.model === r.model) && r.loaded; })
      .sort(function (a, b) { return a.used - b.used; })
      .slice(0, list.length - MAX_FRAMES)
      .forEach(function (r) {
        delete frames[r.model];
        if (r.el.parentNode) r.el.parentNode.removeChild(r.el);
        if (queue.indexOf(r.model) === -1) queue.push(r.model);
      });
  }

  function show(rec) {
    if (!rec || rec === active) return;
    if (waiting) { clearTimeout(waiting.timer); waiting = null; }
    var old = active;
    rec.used = Date.now();
    /* Send the current metal and stone before it appears. */
    if (desired && (rec.metal !== desired.metal || rec.secret !== desired.secret)) post(rec, desired.metal, desired.secret);
    rec.el.style.visibility = 'visible';
    rec.el.style.pointerEvents = '';
    rec.el.style.zIndex = '1';
    rec.el.style.opacity = '1';
    if (old) {
      old.el.id = 'mesh3d-viewer-' + old.model;
      old.el.style.zIndex = '0';
      old.used = Date.now();
    }
    rec.el.id = 'mesh3d-viewer';
    active = rec;
    setTimeout(function () { if (old && old !== active) discard(old); pump(); }, FADE_MS + 50);
    reconcile(active);
  }

  function waitFor(model) {
    if (waiting && waiting.model === model) return;
    if (waiting) clearTimeout(waiting.timer);
    waiting = { model: model };
    waiting.timer = setTimeout(function () {
      if (!waiting || waiting.model !== model) return;
      var rec = frames[model];
      if (rec) { rec.loaded = true; show(rec); }
    }, SWAP_TIMEOUT_MS);
  }

  /* Returns true if handled (caller must not reload the frame). */
  function route(src) {
    var st = parseSrc(src);
    if (!st || !st.model || !active) return false;
    desired = { model: st.model, metal: st.metal, secret: st.secret, src: src };
    if (st.model === active.model) {
      if (waiting) { clearTimeout(waiting.timer); waiting = null; }
      reconcile(active);
      return true;
    }
    /* Stop any warm-up of a different shape, so the shopper's pick gets
       the connection and the graphics card to itself. */
    Object.keys(frames).forEach(function (k) {
      var r = frames[k];
      if (r !== active && k !== st.model) {
        delete frames[k];
        if (r.el.parentNode) r.el.parentNode.removeChild(r.el);
        if (!warmed[k] && queue.indexOf(k) === -1) queue.unshift(k);
      }
    });
    var rec = frames[st.model];
    if (rec && rec.loaded) { show(rec); return true; }
    if (!rec) {
      var qi = queue.indexOf(st.model);
      if (qi !== -1) queue.splice(qi, 1);
      rec = makeFrame(st.model);
      if (!rec) return false;
    }
    waitFor(st.model);
    return true;
  }

  /* ---- Preload queue ---- */
  function shapeModels() {
    if (!active || !active.model) return [];
    var base = active.model.replace(/-[a-z]+$/, '');
    var out = [];
    document.querySelectorAll('input[type="radio"][id*="__main-1-"]').forEach(function (i) {
      var code = SHAPE_CODE[i.value.trim().toLowerCase()];
      var m = code && base + '-' + code;
      if (m && out.indexOf(m) === -1) out.push(m);
    });
    return out;
  }

  function loadingCount() {
    var n = 0;
    Object.keys(frames).forEach(function (k) { if (!frames[k].loaded) n++; });
    return n;
  }

  /* Hold the queue while the shopper is spinning the 360, so loading the
     next shape never competes with the ring they're dragging. Desktop: the
     cursor is over the 360. Phone: they last touched inside the 360 (the
     page loses focus to the frame) within the last HOLD_MS. */
  var HOLD_MS = 2500;               /* KNOB */
  var overViewer = false, touchedAt = 0, holdTimer = null;
  function isViewer(t) { return t && t.tagName === 'IFRAME' && /^mesh3d-viewer/.test(t.id || ''); }
  document.addEventListener('mouseover', function (e) { overViewer = isViewer(e.target); }, true);
  window.addEventListener('blur', function () {
    setTimeout(function () { if (isViewer(document.activeElement)) { touchedAt = Date.now(); setTimeout(pump, HOLD_MS + 50); } }, 0);
  });
  function holding() { return overViewer || (Date.now() - touchedAt < HOLD_MS); }

  function pump() {
    if (!PRELOAD || !preloadStarted) return;
    if (holding()) {
      if (!holdTimer) holdTimer = setTimeout(function () { holdTimer = null; pump(); }, 500);
      return;
    }
    if (loadingCount() > 0) return; /* one at a time, so the shopper's own pick is never starved */
    while (queue.length) {
      if (Object.keys(frames).length >= MAX_FRAMES) return;
      var m = queue.shift();
      if (frames[m] || warmed[m] || (active && m === active.model)) continue;
      makeFrame(m);
      return;
    }
  }

  function startPreload() {
    if (!PRELOAD || preloadStarted) return;
    setTimeout(function () {
      preloadStarted = true;
      queue = shapeModels().filter(function (m) { return !frames[m] && !warmed[m]; });
      pump();
    }, PRELOAD_START_MS);
  }

  /* Hovering a shape (desktop) or touching it (phone) moves it to the front. */
  function bump(e) {
    var lab = e.target && e.target.closest && e.target.closest('label');
    var inp = lab && (lab.control || document.getElementById(lab.getAttribute('for')));
    if (!inp || !/__main-1-/.test(inp.id) || !active) return;
    var code = SHAPE_CODE[inp.value.trim().toLowerCase()];
    if (!code) return;
    var m = active.model.replace(/-[a-z]+$/, '') + '-' + code;
    if (frames[m] || warmed[m]) return;
    var i = queue.indexOf(m);
    if (i > 0) { queue.splice(i, 1); queue.unshift(m); }
    pump();
  }
  document.addEventListener('mouseover', bump, true);
  document.addEventListener('touchstart', bump, { capture: true, passive: true });

  window.addEventListener('message', function (e) {
    var d = e.data;
    if (!d || typeof d !== 'object' || !d.type) return;
    if (String(e.origin).indexOf('mesh3d.com') === -1) return;
    var rec = null;
    Object.keys(frames).forEach(function (k) { if (frames[k].el.contentWindow === e.source) rec = frames[k]; });
    if (!rec) return;
    if (d.type === 'viewer_ready' || d.type === 'loading_complete') {
      rec.ready = true;
      /* Re-send the current pick once the viewer is listening, in case an
         earlier message arrived before it was ready and was dropped. */
      if (desired && rec.model === desired.model) rec.metal = null;
      reconcile(rec);
    }
    if (d.type === 'loading_complete' || d.type === 'viewer_error') {
      var first = !rec.loaded;
      rec.loaded = true;
      rec.ready = true;
      if (rec === active) warmed[rec.model] = true;
      if (waiting && waiting.model === rec.model) show(rec);
      else if (rec !== active) discard(rec);
      if (rec === active && first) startPreload();
      setTimeout(pump, 150);
    }
  });

  /* 3. Keep the 3D thumbnail on the selected shape + metal. Runs just after the
     older sync in snippets/product-media-gallery.liquid (120ms / 700ms), so it
     wins where that one had gaps. */
  var FRONT = null;
  function frontMap() {
    if (FRONT) return FRONT;
    var n = document.getElementById('olvr-front-map');
    try { FRONT = n ? JSON.parse(n.textContent) : {}; } catch (e) { FRONT = {}; }
    return FRONT;
  }
  function syncThumb() {
    var thumb = document.getElementById('viewer-3d-thumb');
    if (!thumb) return;
    var shape = null, metal = null;
    document.querySelectorAll('input[type="radio"]:checked').forEach(function (r) {
      if (/__main-1-/.test(r.id)) shape = r.value.trim();
      if (/__main-2-/.test(r.id)) metal = r.value.trim();
    });
    if (!shape || !metal) return;
    var url = frontMap()[shape + '||' + metal];
    if (url && thumb.getAttribute('src') !== url) { thumb.setAttribute('src', url); thumb.removeAttribute('srcset'); }
  }
  document.addEventListener('change', function (e) {
    if (e.target && e.target.matches && e.target.matches('input[type="radio"]')) {
      setTimeout(syncThumb, 200); setTimeout(syncThumb, 900);
    }
  });

  /* 4. Stop the gallery from shuffling the 360. On every option change Dawn
     (assets/product-info.js updateMedia) calls setActiveMedia(photo, true),
     which moves the variant's photo in front of the 360. The viewer-position
     fix in layout/theme.liquid then moves the 360 back to first place, and
     moving an iframe in the page reloads it from scratch. On the builder we
     keep the 360 as the active slide instead, so nothing moves. */
  /* assets/media-gallery.js keeps its own bound copy of setActiveMedia
     (_origSetActiveMedia) on each gallery, made when the gallery starts up, so
     the copy on the gallery itself is what has to change. Same call, just with
     prepend turned off: the photo is still marked active and the gallery's own
     force3DMain() then snaps back to the 360, exactly as before, but nothing
     gets moved, so the 360 never reloads. */
  function patchGallery() {
    document.querySelectorAll('media-gallery').forEach(function (g) {
      if (g.__olvrNoPrepend || typeof g._origSetActiveMedia !== 'function') return;
      if (!g.querySelector('#mesh3d-viewer')) return;
      var orig = g._origSetActiveMedia;
      g._origSetActiveMedia = function (mediaId, prepend) {
        return orig(mediaId, false);
      };
      g.__olvrNoPrepend = true;
    });
  }
  if (window.customElements) {
    if (customElements.get('media-gallery')) patchGallery();
    else customElements.whenDefined('media-gallery').then(patchGallery);
  }

  /* Leaving the page (Oct 23). Safari keeps the last few pages in memory for
     the back button, 3D viewers included, so tapping through styles (each one is
     a new page) stacked up 3D viewers until the page crashed. Empty the viewers
     as the page is left, and if the shopper comes back to it from memory,
     reload it so the 360 starts fresh. */
  window.addEventListener('pagehide', function (e) {
    if (!e.persisted) return;
    document.querySelectorAll('iframe[id^="mesh3d-viewer"]').forEach(function (f) {
      try { nativeSet(f, 'about:blank'); } catch (err) {}
    });
  });
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) location.reload();
  });

  function start() {
    var el = document.getElementById('mesh3d-viewer');
    if (el) adopt(el);
    patchGallery();
    window.addEventListener('load', patchGallery);
    setTimeout(syncThumb, 800);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  window.OLVRViewer = {
    state: function () {
      return {
        active: active && { model: active.model, metal: active.metal, secret: active.secret, ready: active.ready },
        waiting: waiting && waiting.model,
        warmed: Object.keys(warmed),
        frames: Object.keys(frames),
        loading: Object.keys(frames).filter(function (k) { return !frames[k].loaded; }),
        queue: queue.slice(),
        desired: desired
      };
    }
  };
})();
